import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BranchProvider } from '../context/BranchContext';
import PrivateRoute from './PrivateRoute';
import Layout from './Layout';
import ManagerLayout from './ManagerLayout';
import OwnerLayout from './OwnerLayout';
import SuperAdminLayout from './SuperAdminLayout';
import ErrorBoundary from './ErrorBoundary';
import { withPageTab, SalesTab, ProductsTab, TeamTab, CustomersTab, FinanceTab, SettingsTab } from '../pages/owner/wrappers';
import ManagerSalesTab from '../pages/manager/wrappers/ManagerSalesTab';
import ManagerTeamTab from '../pages/manager/wrappers/ManagerTeamTab';
import ManagerCustomersTab from '../pages/manager/wrappers/ManagerCustomersTab';

// Loading skeleton for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen bg-primaryB-50 dark:bg-primaryB-900 p-4">
    <div className="animate-pulse space-y-4">
      <div className="h-8 bg-primaryB-200 dark:bg-primaryB-700 rounded w-1/4"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="h-32 bg-primaryB-200 dark:bg-primaryB-700 rounded-lg"></div>
        <div className="h-32 bg-primaryB-200 dark:bg-primaryB-700 rounded-lg"></div>
        <div className="h-32 bg-primaryB-200 dark:bg-primaryB-700 rounded-lg"></div>
      </div>
      <div className="h-64 bg-primaryB-200 dark:bg-primaryB-700 rounded-lg"></div>
    </div>
  </div>
);

// Lazy load all page components
const TenantLogin = lazy(() => import('../pages/TenantLogin'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const RetailPOS = lazy(() => import('../pages/RetailPOS'));
const RestaurantPOS = lazy(() => import('../pages/RestaurantPOS'));
const ServicePOS = lazy(() => import('../pages/ServicePOS'));
const KitchenDisplay = lazy(() => import('../pages/KitchenDisplay'));
const Inventory = lazy(() => import('../pages/Inventory'));
const Staff = lazy(() => import('../pages/Staff'));
const Reports = lazy(() => import('../pages/Reports'));
const Audit = lazy(() => import('../pages/Audit'));
const Expenses = lazy(() => import('../pages/Expenses'));
const Settings = lazy(() => import('../pages/Settings'));

// Super Admin pages
const SuperAdminDashboard = lazy(() => import('../pages/superadmin/Dashboard'));
const SuperAdminApplications = lazy(() => import('../pages/superadmin/Applications'));
const SuperAdminTenants = lazy(() => import('../pages/superadmin/Tenants'));
const SuperAdminAnalytics = lazy(() => import('../pages/superadmin/Analytics'));
const SuperAdminOversight = lazy(() => import('../pages/superadmin/Oversight'));
// Subscriptions merged into Tenants page
const SuperAdminBranchRequests = lazy(() => import('../pages/superadmin/BranchRequests'));
const SuperAdminPlatform = lazy(() => import('../pages/superadmin/Platform'));
// Settings merged into Platform page
const SuperAdminMarketIntelligence = lazy(() => import('../pages/superadmin/MarketIntelligence'));
const SuperAdminAuditLog = lazy(() => import('../pages/superadmin/AuditLog'));

// Manager pages
const ManagerDashboard = lazy(() => import('../pages/manager/Dashboard'));
const ManagerEmployees = lazy(() => import('../pages/manager/Employees'));
const ManagerCustomers = lazy(() => import('../pages/manager/Customers'));
const ManagerSales = lazy(() => import('../pages/manager/Sales'));
const ManagerInventory = lazy(() => import('../pages/manager/Inventory'));
const ManagerRequests = lazy(() => import('../pages/manager/Requests'));
const ManagerExpenses = lazy(() => import('../pages/manager/Expenses'));

// Owner pages
const OwnerDashboard = lazy(() => import('../pages/owner/Dashboard'));
const OwnerSales = lazy(() => import('../pages/owner/Sales'));
const OwnerInventory = lazy(() => import('../pages/owner/Inventory'));
const OwnerStaff = lazy(() => import('../pages/owner/Staff'));
const OwnerCustomers = lazy(() => import('../pages/owner/Customers'));
const OwnerRequests = lazy(() => import('../pages/owner/Requests'));
const OwnerExpenses = lazy(() => import('../pages/owner/Expenses'));
const OwnerActivityLogs = lazy(() => import('../pages/owner/ActivityLogs'));
const OwnerReports = lazy(() => import('../pages/owner/Reports'));
const OwnerBranches = lazy(() => import('../pages/owner/Branches'));
const OwnerSettings = lazy(() => import('../pages/owner/Settings'));
const OwnerStockTransfers = lazy(() => import('../pages/owner/StockTransfers'));
const OwnerAttendants = lazy(() => import('../pages/owner/Attendants'));
const OwnerTables = lazy(() => import('../pages/owner/Tables'));
const OwnerReturns = lazy(() => import('../pages/owner/Returns'));
const OwnerRawMaterials = lazy(() => import('../pages/owner/RawMaterials'));
const OwnerStore = lazy(() => import('../pages/owner/Store'));

// Wrapped manager pages – each gets its tab bar rendered above the page
const WrappedManagerSales = withPageTab(ManagerSalesTab, ManagerSales);
const WrappedManagerRequests = withPageTab(ManagerSalesTab, ManagerRequests);
const WrappedManagerEmployees = withPageTab(ManagerTeamTab, ManagerEmployees);
const WrappedManagerAttendants = withPageTab(ManagerTeamTab, OwnerAttendants);
const WrappedManagerCustomers = withPageTab(ManagerCustomersTab, ManagerCustomers);
const WrappedManagerReturns = withPageTab(ManagerCustomersTab, OwnerReturns);

// Wrapped owner pages – each gets its tab bar rendered above the page
const WrappedOwnerSales = withPageTab(SalesTab, OwnerSales);
const WrappedOwnerRequests = withPageTab(SalesTab, OwnerRequests);
const WrappedOwnerInventory = withPageTab(ProductsTab, OwnerInventory);
const WrappedOwnerRawMaterials = withPageTab(ProductsTab, OwnerRawMaterials);
const WrappedOwnerTables = withPageTab(ProductsTab, OwnerTables);
const WrappedOwnerStaff = withPageTab(TeamTab, OwnerStaff);
const WrappedOwnerAttendants = withPageTab(TeamTab, OwnerAttendants);
const WrappedOwnerCustomers = withPageTab(CustomersTab, OwnerCustomers);
const WrappedOwnerReturns = withPageTab(CustomersTab, OwnerReturns);
const WrappedOwnerExpenses = withPageTab(FinanceTab, OwnerExpenses);
const WrappedOwnerStockTransfers = withPageTab(ProductsTab, OwnerStockTransfers);
const WrappedOwnerReports = withPageTab(FinanceTab, OwnerReports);
const WrappedOwnerSettings = withPageTab(SettingsTab, OwnerSettings);
const WrappedOwnerBranches = withPageTab(SettingsTab, OwnerBranches);
const WrappedOwnerActivityLogs = withPageTab(SettingsTab, OwnerActivityLogs);

// Map legacy business types to main categories
const getBusinessCategory = (businessType) => {
  const retailTypes = ['RETAIL', 'GROCERY', 'ELECTRONICS', 'PHARMACY', 'CLOTHING'];
  const serviceTypes = ['SERVICES', 'SALON'];
  const foodTypes = ['FOOD_AND_BEVERAGE', 'RESTAURANT'];

  if (retailTypes.includes(businessType)) return 'RETAIL';
  if (serviceTypes.includes(businessType)) return 'SERVICES';
  if (foodTypes.includes(businessType)) return 'FOOD_AND_BEVERAGE';
  return businessType;
};

// Embedded POS component - renders the appropriate POS based on business type
const EmbeddedPOS = (props) => {
  const { user } = useAuth();
  const category = getBusinessCategory(user?.businessType);

  if (category === 'RETAIL') {
    return <RetailPOS embedded={true} {...props} />;
  } else if (category === 'FOOD_AND_BEVERAGE') {
    return <RestaurantPOS embedded={true} {...props} />;
  } else if (category === 'SERVICES') {
    return <ServicePOS embedded={true} {...props} />;
  }
  return <p>Unknown business type: {user?.businessType || 'not set'}</p>;
};

// Memoized tab wrapper – only re-renders when isActive or props actually change
const KeepAliveTab = React.memo(({ isActive, component: Component, extraProps, tabProps }) => (
  <div style={{ display: isActive ? 'contents' : 'none' }}>
    <Suspense fallback={<PageLoader />}>
      <Component {...extraProps} {...tabProps} />
    </Suspense>
  </div>
));

// Keep-alive tab renderer – mounts tabs once visited, hides inactive
const KeepAliveTabs = ({ tabs, activeTab, extraProps }) => {
  const [visited, setVisited] = useState(() => new Set([activeTab]));

  useEffect(() => {
    if (activeTab && !visited.has(activeTab)) {
      setVisited(prev => new Set(prev).add(activeTab));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {tabs.map(({ key, component, props: tabProps }) => {
        if (!visited.has(key)) return null;
        return (
          <KeepAliveTab
            key={key}
            isActive={activeTab === key}
            component={component}
            extraProps={extraProps}
            tabProps={tabProps}
          />
        );
      })}
    </>
  );
};

// Resolves the active sub-path from location (supports nested paths like "sales/voids")
const useSubPath = (prefix) => {
  const location = useLocation();
  const { slug } = useParams();
  const fullPrefix = `/${slug}/${prefix}/`;
  if (!location.pathname.startsWith(fullPrefix)) return '';
  // Return the full remaining path, not just the first segment
  const remaining = location.pathname.slice(fullPrefix.length);
  // Strip trailing slash if any
  return remaining.replace(/\/$/, '') || '';
};

// Sequential chunk prefetcher with priority ordering
// - Waits for initial delay so the current page's API calls finish first
// - Loads chunks one at a time (no bandwidth competition)
// - Uses requestIdleCallback between chunks so the UI stays responsive
const usePrefetchChunks = (importFns, initialDelay = 2000) => {
  useEffect(() => {
    let cancelled = false;

    const runQueue = async () => {
      // Wait for the current page to settle (API calls, initial render)
      await new Promise(r => setTimeout(r, initialDelay));

      for (const fn of importFns) {
        if (cancelled) break;
        // Wait for idle time before loading next chunk
        await new Promise(r => {
          if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(r, { timeout: 3000 });
          } else {
            setTimeout(r, 150);
          }
        });
        if (cancelled) break;
        // Load chunk, wait for it to finish before starting next
        await fn().catch(() => {});
      }
    };

    runQueue();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

// Owner chunks – ordered by priority (most likely to be clicked first)
// 1. POS (heaviest, most important)
// 2. Sales, Inventory (frequent tabs)
// 3. Everything else
const OWNER_CHUNK_IMPORTS = [
  // Priority 1 – POS (user's next likely click)
  () => import('../pages/RetailPOS'),
  () => import('../pages/RestaurantPOS'),
  () => import('../pages/ServicePOS'),
  // Priority 2 – frequent tabs
  () => import('../pages/owner/Sales'),
  () => import('../pages/owner/Inventory'),
  () => import('../pages/owner/Expenses'),
  () => import('../pages/owner/Reports'),
  // Priority 3 – less frequent
  () => import('../pages/owner/Staff'),
  () => import('../pages/owner/Customers'),
  () => import('../pages/owner/Requests'),
  () => import('../pages/owner/Settings'),
  () => import('../pages/owner/ActivityLogs'),
  () => import('../pages/owner/Branches'),
  () => import('../pages/owner/StockTransfers'),
  () => import('../pages/owner/Attendants'),
  () => import('../pages/owner/Tables'),
  () => import('../pages/owner/Returns'),
  () => import('../pages/owner/RawMaterials'),
];

const MANAGER_CHUNK_IMPORTS = [
  // Priority 1 – POS
  () => import('../pages/RetailPOS'),
  () => import('../pages/RestaurantPOS'),
  () => import('../pages/ServicePOS'),
  // Priority 2 – frequent tabs
  () => import('../pages/manager/Sales'),
  () => import('../pages/manager/Inventory'),
  () => import('../pages/manager/Expenses'),
  // Priority 3 – less frequent
  () => import('../pages/manager/Employees'),
  () => import('../pages/manager/Customers'),
  () => import('../pages/manager/Requests'),
  () => import('../pages/owner/Attendants'),
  () => import('../pages/owner/Returns'),
];

// Owner page router – receives layout props from OwnerLayout via cloneElement
const OwnerPageRouter = (props) => {
  const subPath = useSubPath('owner');
  usePrefetchChunks(OWNER_CHUNK_IMPORTS);

  const ALL_TABS = [
    { key: 'dashboard', component: OwnerDashboard },
    { key: 'pos', component: EmbeddedPOS },
    // Sales (consolidated: revenue + voids)
    { key: 'sales', component: WrappedOwnerSales },
    { key: 'sales/voids', component: WrappedOwnerRequests },
    // Products (consolidated: inventory + raw materials + tables + transfers)
    { key: 'products', component: WrappedOwnerInventory },
    { key: 'products/raw-materials', component: WrappedOwnerRawMaterials },
    { key: 'products/tables', component: WrappedOwnerTables },
    { key: 'products/transfers', component: WrappedOwnerStockTransfers },
    // Team (consolidated: staff + attendants)
    { key: 'team', component: WrappedOwnerStaff },
    { key: 'team/attendants', component: WrappedOwnerAttendants },
    // Customers (consolidated: customers + returns)
    { key: 'customers', component: WrappedOwnerCustomers },
    { key: 'customers/returns', component: WrappedOwnerReturns },
    // Finance (consolidated: expenses + reports)
    { key: 'finance', component: WrappedOwnerExpenses },
    { key: 'finance/reports', component: WrappedOwnerReports },
    // Store (consolidated: overview + orders + editor)
    { key: 'store', component: OwnerStore },
    // Settings (consolidated: settings + branches + activity logs)
    { key: 'settings', component: WrappedOwnerSettings },
    { key: 'settings/branches', component: WrappedOwnerBranches },
    { key: 'settings/activity', component: WrappedOwnerActivityLogs },
  ];

  // Legacy route redirects – map old paths to new consolidated paths
  const LEGACY_REDIRECTS = {
    'inventory': 'products',
    'raw-materials': 'products/raw-materials',
    'tables': 'products/tables',
    'staff': 'team',
    'attendants': 'team/attendants',
    'returns': 'customers/returns',
    'expenses': 'finance',
    'stock-transfers': 'products/transfers',
    'reports': 'finance/reports',
    'branches': 'settings/branches',
    'activity': 'settings/activity',
    'requests': 'sales/voids',
  };

  if (LEGACY_REDIRECTS[subPath]) {
    return <OwnerLegacyRedirect />;
  }

  return <KeepAliveTabs tabs={ALL_TABS} activeTab={subPath} extraProps={props} />;
};

// Legacy owner route redirector – maps old paths to new consolidated paths
const OwnerLegacyRedirect = () => {
  const { slug } = useParams();
  const location = useLocation();
  const oldPath = location.pathname.replace(`/${slug}/owner/`, '');

  const LEGACY_MAP = {
    'inventory': 'products',
    'raw-materials': 'products/raw-materials',
    'tables': 'products/tables',
    'staff': 'team',
    'attendants': 'team/attendants',
    'returns': 'customers/returns',
    'expenses': 'finance',
    'stock-transfers': 'products/transfers',
    'reports': 'finance/reports',
    'branches': 'settings/branches',
    'activity': 'settings/activity',
    'requests': 'sales/voids',
  };

  const newPath = LEGACY_MAP[oldPath];
  if (newPath) {
    return <Navigate to={`/${slug}/owner/${newPath}`} replace />;
  }
  return <Navigate to={`/${slug}/owner/dashboard`} replace />;
};

// Owner Routes – single Layout that stays mounted, pages switch inside
const OwnerRoutes = () => {
  return (
    <BranchProvider>
      <ErrorBoundary>
        <OwnerLayout>
          <OwnerPageRouter />
        </OwnerLayout>
      </ErrorBoundary>
    </BranchProvider>
  );
};

// Manager legacy route redirector
const ManagerLegacyRedirect = () => {
  const { slug } = useParams();
  const location = useLocation();
  const oldPath = location.pathname.replace(`/${slug}/manager/`, '');

  const LEGACY_MAP = {
    'requests': 'sales/requests',
    'employees': 'team',
    'attendants': 'team/attendants',
    'returns': 'customers/returns',
  };

  const newPath = LEGACY_MAP[oldPath];
  if (newPath) {
    return <Navigate to={`/${slug}/manager/${newPath}`} replace />;
  }
  return <Navigate to={`/${slug}/manager/dashboard`} replace />;
};

// Manager page router – receives layout props from ManagerLayout via cloneElement
const ManagerPageRouter = (props) => {
  const subPath = useSubPath('manager');
  usePrefetchChunks(MANAGER_CHUNK_IMPORTS);

  const ALL_TABS = [
    { key: 'dashboard', component: ManagerDashboard },
    { key: 'pos', component: EmbeddedPOS, props: { managerView: true } },
    // Sales (consolidated: revenue + void requests)
    { key: 'sales', component: WrappedManagerSales },
    { key: 'sales/requests', component: WrappedManagerRequests },
    // Inventory
    { key: 'inventory', component: ManagerInventory },
    // Team (consolidated: staff + attendants)
    { key: 'team', component: WrappedManagerEmployees },
    { key: 'team/attendants', component: WrappedManagerAttendants },
    // Customers (consolidated: customers + returns)
    { key: 'customers', component: WrappedManagerCustomers },
    { key: 'customers/returns', component: WrappedManagerReturns },
    // Expenses
    { key: 'expenses', component: ManagerExpenses },
    // Store
    { key: 'store', component: OwnerStore },
  ];

  const LEGACY_REDIRECTS = {
    'requests': 'sales/requests',
    'employees': 'team',
    'attendants': 'team/attendants',
    'returns': 'customers/returns',
  };

  if (LEGACY_REDIRECTS[subPath]) {
    return <ManagerLegacyRedirect />;
  }

  return <KeepAliveTabs tabs={ALL_TABS} activeTab={subPath} extraProps={props} />;
};

// Manager Routes – single Layout that stays mounted
const ManagerRoutes = () => {
  return (
    <BranchProvider>
      <ErrorBoundary>
        <ManagerLayout>
          <ManagerPageRouter />
        </ManagerLayout>
      </ErrorBoundary>
    </BranchProvider>
  );
};

// Role-based Dashboard component
const RoleBasedDashboard = () => {
  const { slug } = useParams();
  const { user, isCashier, isManager, isOwner } = useAuth();
  const category = getBusinessCategory(user?.businessType);

  // Cashiers get the POS interface based on business type
  if (isCashier) {
    if (category === 'RETAIL') {
      return (
        <BranchProvider>
          <Suspense fallback={<PageLoader />}>
            <RetailPOS />
          </Suspense>
        </BranchProvider>
      );
    } else if (category === 'FOOD_AND_BEVERAGE') {
      return (
        <BranchProvider>
          <Suspense fallback={<PageLoader />}>
            <RestaurantPOS />
          </Suspense>
        </BranchProvider>
      );
    } else if (category === 'SERVICES') {
      return (
        <BranchProvider>
          <Suspense fallback={<PageLoader />}>
            <ServicePOS />
          </Suspense>
        </BranchProvider>
      );
    }
    // Fallback for cashiers if businessType is not matched (shouldn't happen with proper setup)
    return <p>Error: Unknown Business Type ({user?.businessType || 'not set'})</p>;
  }

  // Managers get redirected to manager dashboard
  if (isManager) {
    return <Navigate to={`/${slug}/manager/dashboard`} replace />;
  }

  // Owners get redirected to owner dashboard
  if (isOwner) {
    return <Navigate to={`/${slug}/owner/dashboard`} replace />;
  }

  // Fallback to regular dashboard
  return (
    <Suspense fallback={<PageLoader />}>
      <Layout>
        <Dashboard />
      </Layout>
    </Suspense>
  );
};

// Validates that logged-in user belongs to this tenant
const TenantGuard = ({ children }) => {
  const { slug } = useParams();
  const { user } = useAuth();

  // Super admin accessing /admin routes
  if (slug === 'admin' && user?.isSuperAdmin) {
    return children;
  }

  // Regular user - check tenant slug matches
  if (user && user.tenantSlug && user.tenantSlug !== slug) {
    // User is logged in but to a different tenant - redirect to their tenant
    return <Navigate to={`/${user.tenantSlug}/dashboard`} replace />;
  }

  return children;
};

const TenantRoutes = () => {
  const { slug } = useParams();
  const { user, isSuperAdmin } = useAuth();

  // Super admin routes
  if (slug === 'admin') {
    return (
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="login" element={<TenantLogin />} />
        <Route
          path="dashboard"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminDashboard />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="applications"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminApplications />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="tenants"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminTenants />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminAnalytics />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="oversight"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminOversight />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        {/* Subscriptions merged into Tenants page – redirect for old bookmarks */}
        <Route path="subscriptions" element={<Navigate to="/admin/tenants" replace />} />
        <Route
          path="branch-requests"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminBranchRequests />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="platform"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminPlatform />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        {/* Settings merged into Platform – redirect */}
        <Route path="settings" element={<Navigate to="/admin/platform" replace />} />
        <Route
          path="market-intelligence"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminMarketIntelligence />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="audit-log"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminLayout>
                <SuperAdminAuditLog />
              </SuperAdminLayout>
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to={`/admin/${user ? 'dashboard' : 'login'}`} replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    );
  }

  // Regular tenant routes
  return (
    <TenantGuard>
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Login */}
        <Route path="login" element={<TenantLogin />} />

        {/* Dashboard - Role-based */}
        <Route
          path="dashboard"
          element={
            <PrivateRoute>
              <RoleBasedDashboard />
            </PrivateRoute>
          }
        />

        {/* Manager routes - all wrapped in single BranchProvider */}
        <Route
          path="manager/*"
          element={
            <PrivateRoute>
              <ManagerRoutes />
            </PrivateRoute>
          }
        />

        {/* Kitchen Display (Restaurant Mode) */}
        <Route
          path="kitchen"
          element={
            <PrivateRoute>
              <KitchenDisplay />
            </PrivateRoute>
          }
        />

        {/* Owner routes - all wrapped in single BranchProvider */}
        <Route
          path="owner/*"
          element={
            <PrivateRoute requireOwner>
              <OwnerRoutes />
            </PrivateRoute>
          }
        />

        {/* Legacy routes (without /owner or /manager prefix) */}
        <Route
          path="inventory"
          element={
            <PrivateRoute requireAdmin>
              <Layout>
                <Inventory />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="staff"
          element={
            <PrivateRoute requireAdmin>
              <Layout>
                <Staff />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="reports"
          element={
            <PrivateRoute requireAdmin>
              <Layout>
                <Reports />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="expenses"
          element={
            <PrivateRoute requireAdmin>
              <Layout>
                <Expenses />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="audit"
          element={
            <PrivateRoute requireAdmin>
              <Layout>
                <Audit />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="settings"
          element={
            <PrivateRoute requireAdmin>
              <Layout>
                <Settings />
              </Layout>
            </PrivateRoute>
          }
        />

        {/* Default redirect to dashboard or login */}
        <Route path="*" element={<Navigate to={`/${slug}/${user ? 'dashboard' : 'login'}`} replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </TenantGuard>
  );
};

export default TenantRoutes;
